import { useState, type ReactNode } from 'react';

/**
 * Production's **two-step inline confirmation** (spec §8).
 *
 * Every mutating control on the console is this shape, and it is the same shape
 * for all five: the first click swaps the cell to `Confirm` / `Cancel`, and the
 * pending state relabels the button. There is no modal, no dialog, and no
 * typed confirmation — production has none of those, and adding one would
 * change how an operator works.
 *
 * **The cell stops propagation.** A user row opens when clicked, so an action
 * inside it would toggle the row as well as act. Production's actions cell
 * calls `stopPropagation`, and so does this: acting never opens or closes
 * anything.
 */
export function ConfirmAction({
  label,
  pendingLabel,
  onConfirm,
  onChanged,
  testId,
  children,
}: {
  /** The resting label. `children` renders it instead when it is an icon. */
  label: string;
  /** Shown while the write is in flight: `Granting...`, `Archiving...`. */
  pendingLabel: string;
  onConfirm: () => Promise<boolean>;
  /** Called after a write that succeeded, so the console can reload. */
  onChanged: () => void;
  testId: string;
  /** An icon, for the delete control production renders as a trash can. */
  children?: ReactNode;
}) {
  const [armed, setArmed] = useState(false);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  async function confirm() {
    setPending(true);
    setFailed(false);
    const ok = await onConfirm();
    setPending(false);
    setArmed(false);
    if (ok) onChanged();
    else setFailed(true);
  }

  return (
    <span
      className="inline-flex items-center gap-2"
      data-testid={testId}
      // Production's actions cell does exactly this. Without it, every click on
      // a control would also toggle the row it sits in.
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {pending ? (
        <span className="text-xs text-muted-foreground" data-testid={`${testId}-pending`}>
          {pendingLabel}
        </span>
      ) : armed ? (
        <>
          <button
            type="button"
            onClick={() => void confirm()}
            data-testid={`${testId}-confirm`}
            className="rounded-md border border-border px-2 py-1 text-xs font-medium transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setArmed(false)}
            data-testid={`${testId}-cancel`}
            className="rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            Cancel
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setArmed(true)}
          aria-label={label}
          data-testid={`${testId}-arm`}
          className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {children ?? label}
        </button>
      )}

      {failed ? (
        <span className="text-xs font-medium" data-testid={`${testId}-error`} role="status">
          Error loading admin data
        </span>
      ) : null}
    </span>
  );
}
